import { type UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { type AxiosError, type AxiosResponse } from "axios";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  selectedClientServiceIdAtom,
  selectedOrganisationIdAtom,
  userAtom,
} from "@/store";

type AppError = AxiosError<{
  status: number;
  cause?: null | {
    suggestedFixData: {
      organisationId?: number | null;
      clientServiceId?: number | null;
    };
  };
}>;

export const useRedirection = <T>(params: {
  queryKeysToInvalidate: Array<string>;

  queryResult?: UseQueryResult<AxiosResponse<T>, Error | AppError>;
}) => {
  const _queryResult = params.queryResult as UseQueryResult<
    AxiosResponse<T>,
    AxiosError<{
      status: number;
      cause?: null | {
        suggestedFixData: {
          organisationId?: number | null;
          clientServiceId?: number | null;
        };
      };
      suggestedFixData?: {
        organisationId?: number | null;
        clientServiceId?: number | null;
      };
    }>
  >;

  const [selectedOrganisationIdAtomValue, setSelectedOrganisationIdAtomValue] =
    useAtom(selectedOrganisationIdAtom);
  const [
    selectedClientServiceIdAtomValue,
    setSelectedClientServiceIdAtomValue,
  ] = useAtom(selectedClientServiceIdAtom);
  const userAtomValue = useAtomValue(userAtom);

  const [initialSelectedOrganisationId] = useState<number>(
    selectedOrganisationIdAtomValue
  );
  const [initialSelectedServiceId] = useState<number>(
    selectedClientServiceIdAtomValue
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (selectedClientServiceIdAtomValue !== initialSelectedServiceId) {
      void queryClient.invalidateQueries({
        queryKey: params.queryKeysToInvalidate,
      });
    }
  }, [
    selectedClientServiceIdAtomValue,
    initialSelectedServiceId,
    params.queryKeysToInvalidate,
    queryClient,
  ]);

  useEffect(() => {
    if (selectedOrganisationIdAtomValue !== initialSelectedOrganisationId) {
      void queryClient.invalidateQueries({
        queryKey: params.queryKeysToInvalidate,
      });
    }
  }, [
    selectedOrganisationIdAtomValue,
    initialSelectedOrganisationId,
    params.queryKeysToInvalidate,
    queryClient,
  ]);

  useEffect(() => {
    if (!params.queryResult) {
      return;
    }

    let foundSuggestedFixes: boolean = false;
    const axiosError = params.queryResult?.error as AppError;
    const status = axiosError?.response?.status ?? axiosError?.status;
    const responseData = axiosError?.response?.data;

    if (status === 404) {
      const cause = responseData?.cause;
      const suggestedClientServiceId = cause?.suggestedFixData?.clientServiceId;
      const suggestedOrgId = cause?.suggestedFixData?.organisationId;

      if (
        typeof cause === "object" &&
        suggestedOrgId &&
        suggestedOrgId !== initialSelectedOrganisationId &&
        userAtomValue?.Services.map(
          (userService) => userService.Organisation
        ).find((orgObj) => orgObj.id === suggestedOrgId)
      ) {
        foundSuggestedFixes = true;
        setSelectedOrganisationIdAtomValue(suggestedOrgId);
      }

      const userHasAccessToService = userAtomValue?.Services.find(
        (userService) => userService.id === suggestedClientServiceId
      );

      if (
        typeof cause === "object" &&
        suggestedClientServiceId &&
        suggestedClientServiceId !== initialSelectedServiceId &&
        userHasAccessToService
      ) {
        foundSuggestedFixes = true;
        setSelectedClientServiceIdAtomValue(suggestedClientServiceId);
      }
    }

    if (status === 403 && !foundSuggestedFixes) {
      void navigate("/403");
    }

    if (status === 404 && !foundSuggestedFixes) {
      void navigate("/404");
    }
  }, [
    initialSelectedOrganisationId,
    initialSelectedServiceId,
    navigate,
    params.queryResult,
    setSelectedClientServiceIdAtomValue,
    setSelectedOrganisationIdAtomValue,
    userAtomValue?.Services,
  ]);
};
